import { html, css } from 'lit';
import { RecommendationItem, Recommendations } from '../../../server/types.js';
import { canFavorite, getItemBadge, trackClickEvent } from '../utils/index.js';
import './recommendation-badge-ab-test.js';
import '../favorite-heart.js';
import { i18n } from '@lingui/core';
import WarpElement from '@warp-ds/elements-core';
import { customElement } from 'lit/decorators/custom-element.js';

@customElement('recommendation-card-title-first-badge-outside')
export class RecommendationCardTitleFirstBadgeOutside extends WarpElement {
	static styles = [
		...WarpElement.styles,
		css`
			.two-lines {
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 2;
				overflow: hidden;
			}

			.one-line {
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 1;
				overflow: hidden;
			}

			/** The line below is where the injected CSS goes, removing it means you get no CSS from the design system **/
			*,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.text-body{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}.aspect-square{padding-bottom:100%;position:relative}.aspect-square>*{width:100%;height:100%;position:absolute;inset:0}.static{position:static}.active\:s-bg-active:active{background-color:var(--w-s-color-background-active)}.pt-4{padding-top:.4rem}.break-all{word-break:break-all}.object-cover{object-fit:cover}.object-scale-down{object-fit:scale-down}.object-center{object-position:center}.text-ellipsis{text-overflow:ellipsis}@media (width>=480px){.sm\:h-auto{height:auto}};
		`,
	];

	item!: RecommendationItem;
	data!: Recommendations;
	imgCdnUrl!: string;
	favoriteBaseUrl!: string;
	loginId!: string;

	static properties = {
		item: { type: Object },
		data: { type: Object },
		imgCdnUrl: { type: String },
		favoriteBaseUrl: { type: String },
		loginId: { type: String },
		locale: { type: String },
	};

	render() {
		return html`<article class="relative" aria-labelledby="title-first-badge-outside-card-title">
			<base-card .linkUrl="${this.item.url}" @base-card-click="${() => trackClickEvent(this.item, this.data)}">
				<div class="relative flex flex-col">
					<div class="flex flex-col">
						${this.renderImage()}
						${this.item.heading
							? html` <!-- 2. Title -->
									<h3
										id="price-first-card-title"
										class="s-text text-body font-normal ${this.item.type && this.item.type.toUpperCase() !== 'JOB' &&
										this.item.type.toUpperCase() !== 'CMS'
											? 'pt-4'
											: 'pt-8'} mb-0 break-words text-ellipsis two-lines"
									>
										${this.item?.heading}
									</h3>`
							: ''}
						${this.item.type && this.item.type.toUpperCase() !== 'JOB' && this.item.type.toUpperCase() !== 'CMS'
							? this.item.label
								? html`
										<!-- 3. Price -->
										<div class="font-bold s-text pt-8">${this.item.label}</div>
									`
								: ''
							: ''}
						${this.item.customLabel ? html` <div class="text-s s-text-subtle">${this.item.customLabel}</div>` : ''}
						${this.item.content?.company_name
							? html` <!-- 4. Additional info -->
									<div class="text-s s-text-subtle pt-8 text-ellipsis one-line break-all">
										${this.item.content?.company_name}
									</div>`
							: ''}
						${this.item.location?.combined
							? html` <!-- 5. Location -->
									<div
										class="text-s s-text-subtle break-words text-ellipsis ${this.item.type && this.item.type.toUpperCase() === 'JOB' ||
										(this.item.type.toUpperCase() === 'REALESTATE' && this.item.content?.company_name)
											? ''
											: 'pt-8'} two-lines"
									>
										${this.item.location?.combined}
									</div>`
							: ''}
						${getItemBadge(this.item.type, this.item.overlay, this.item.shippingOption) !== undefined ? html`
						<!-- 6. Badge -->
						<recommendation-badge-ab-test
							class="pt-8"
							.badge="${getItemBadge(this.item.type, this.item.overlay, this.item.shippingOption)}"
						></recommendation-badge-ab-test>
						` 
							: ''}
					</div>
				</div>
			</base-card>
			${canFavorite(this.item)
				? html` <!-- 7. Favorite button -->
						<favorite-heart
							.item="${this.item}"
							.favoriteBaseUrl="${this.favoriteBaseUrl}"
							.loginId="${this.loginId}"
							locale="${this.locale}"
							position="top-0 right-0"
						></favorite-heart>`
				: ''}
		</article> `;
	}

	private renderImage() {
		if (!this.item.image) return;
		const isLogo = this.item.image.type === 'LOGO';
		const imageClass = isLogo
			? 'p-16 w-full h-full object-center object-scale-down m-auto sm:h-auto'
			: 'w-full h-full object-center object-cover m-auto';
		const imageSrc =
			this.item.image.isAbsolute || this.item.image.type === 'EXTERNAL'
				? this.item.image.url
				: `${this.imgCdnUrl}/${this.item.image.url}`;

		return html`
			<!-- 1. Image -->
			<div
				class="aspect-square overflow-hidden ${this.item.type?.toUpperCase() === 'JOB'
					? `border`
					: ''} rounded-8 s-bg active:s-bg-active"
			>
				<img
					class="${imageClass}"
					src="${imageSrc}"
					alt="${i18n.t({ id: 'frontpage.recommendations.images.alt.text', message: 'Photo from ad' })}"
				/>
			</div>
		`;
	}
}
